export default class AbstractMessageModalController {
    constructor($uibModalInstance, $injector, $scope, extendedInformation) {
        this.modalInstance = $uibModalInstance;
        this.injector = $injector;
        this.scope = $scope;
        this.extendedInformation = _.defaults(extendedInformation, {
            preSelectedTemplateId: null
        });

        this.alert = this.injector.get('Alert');
        this.fileSaver = this.injector.get('FileSaver');
        this.api = this.injector.get('$http');
        this.translate = this.injector.get('$translate');
        this.state = this.injector.get('$state');
        this.timeout = this.injector.get('$timeout');

        this.submitting = false;

        this.customContent = '';

        this.submitEnabled = true;
        this.selectedTemplateId = null;
        this.fullTemplateInformation = null;

        this.fullTemplates = [];
        this.availableTemplates = [];
        this.extendedMessageParameters = [];
        this.extendedMessageParameterModels = {};
        this.specialFilterInformation = [];

        this.previewLoading = true;
        this.previewData = {};
        this.previewAttachments = [];
        this.previewSize = 'desktop';
        this.currentPreviewCounter = 0;
    }

    $onInit() {
        this._initWatchers();
        this._loadTemplates();
        this._prepareSpecialFilterInformation();
        this._prepareExtendedMessageParameters();
    }

    changePreviewSize(newSize) {
        this.previewSize = newSize;
    }

    dismiss() {
        this.modalInstance.close();
    }

    handleTemplateSelection(selectedTemplate) {
        this.selectedTemplateId = selectedTemplate ? selectedTemplate : null;
        this.fullTemplateInformation = null;
        if (this.selectedTemplateId !== null) {
            this.fullTemplateInformation = _.first(_.filter(this.fullTemplates, (template) => {
                return template.id === parseInt(selectedTemplate);
            }));
        }

        this._prepareExtendedMessageParameters();
        this.scope.$applyAsync();
    }

    _initWatchers() {
        this.scope.$watch(
            () => this.selectedTemplateId,
            () => {
                this._checkIfSubmitEnabled();
                this._prepareExtendedMessageParameters();
                this._updatePreview();
            }
        );

        this.scope.$watch(
            () => this.extendedMessageParameterModels,
            () => {
                this._updatePreviewAttachments();
            },
            true
        );

        this.scope.$watch(
            () => this.customContent,
            () => {
                const currentCustomContent = this.customContent;

                this.timeout(() => {
                    if(currentCustomContent === this.customContent) {
                        this._updatePreview();
                    }
                }, 400);
            },
            true
        );
    }

    _prepareSpecialFilterInformation() {
        this.specialFilterInformation = [];
    }

    _prepareExtendedMessageParameters() {
        this.extendedMessageParameters = this._collectExtendedMessageParameters();

        this._prepareExtendedMessageParameterModels();
    }

    _collectExtendedMessageParameters() {
        const out = [];

        if (
            this.fullTemplateInformation
            && (
                this.fullTemplateInformation.attachment_1
                || this.fullTemplateInformation.attachment_2
                || this.fullTemplateInformation.attachment_3
            )
        ) {
            out.push({
                key: 'attach_files',
                label: 'Attach Files:',
                default: true
            });
        }

        return out;
    }

    _prepareExtendedMessageParameterModels() {
        const newParameterModels = {};

        _.each(this.extendedMessageParameters, (param) => {
            newParameterModels[param.key] = _.get(param, 'default', false);
        })

        this.extendedMessageParameterModels = newParameterModels;
    }

    _checkIfSubmitEnabled() {
        this.submitEnabled = this._isSubmitEnabled();
    }

    _isSubmitEnabled() {
        return !!this.selectedTemplateId;
    }

    _loadTemplates() {
        const store = this.injector.get('Templates').reset(this._getParametersForTemplatesStore());
        this.availableTemplates = [];
        this.fullTemplates = [];

        return store.dispatch('getData')
            .then(() => {
                this.fullTemplates = _.cloneDeep(store.state.data);
                if (this.fullTemplates.length === 0) {
                    this._handleEmptyTemplates();
                }
                this.availableTemplates = _.map(this.fullTemplates, (value, key) => {
                    return {
                        value: value.id,
                        label: value.name
                    }
                });

                this.selectedTemplateId = this.extendedInformation.preSelectedTemplateId ? this.extendedInformation.preSelectedTemplateId : _.get(_.first(this.availableTemplates), 'value', null);
                this.scope.$applyAsync();
            });
    }

    _getParametersForTemplatesStore() {
        return {
            listview: 'SelectTemplateForMessageListView',
            persist: false,
            onlyGlobal: false,
            onlyScoped: false,
            scopedGuestlists: [],
            pagination: {
                perPage: -1
            },
            columns: [
                {
                    key: "id",
                    type: "integer",
                    visible: true
                },
                {
                    key: "name",
                    type: "string",
                    visible: true
                },
                {
                    key: "supports_custom_content",
                    type: "boolean",
                    visible: true
                },
                {
                    key: "global_template",
                    type: "boolean",
                    visible: true
                },
                {
                    key: "bcc",
                    type: "string",
                    visible: true
                },
                {
                    key: "reply_to",
                    type: "string",
                    visible: true
                },
                {
                    key: "attachment_1",
                    type: "file",
                    visible: true
                },
                {
                    key: "attachment_2",
                    type: "file",
                    visible: true
                },
                {
                    key: "attachment_3",
                    type: "file",
                    visible: true
                },
                {
                    key: "email",
                    type: "string",
                    visible: true
                },
                {
                    key: "sender_name",
                    type: "string",
                    visible: true
                }
            ]
        }
    }

    _updatePreviewAttachments() {
        this.previewAttachments = [];

        if (this.extendedMessageParameterModels.attach_files) {
            for (let i = 1; i < 4; i++) {
                const currentAttachment = this.fullTemplateInformation[`attachment_${i}`];
                if (currentAttachment) {
                    this.previewAttachments.push({
                        name: currentAttachment.name,
                        action: () => {
                            this.api
                                .get(currentAttachment.url, {responseType: "blob"})
                                .then(({data, headers}) => {
                                    const blob = new Blob([data], {type: headers('content-type')});

                                    this.fileSaver.saveAs(blob, currentAttachment.name);
                                })
                                .catch(err => this.alert.handle(err));
                        }
                    })
                }
            }
        }

        this.scope.$applyAsync();
    }

    _updatePreview() {
        this.previewData = null;
        this.previewLoadError = false;
        const currentSelectedRecipient = this._getPreviewRecipient();

        this.currentPreviewCounter = this.currentPreviewCounter+1;
        const nowPreviewCounter = this.currentPreviewCounter;

        if (!currentSelectedRecipient || !this.selectedTemplateId) {
            return;
        }

        let url = this._getPreviewUrl(currentSelectedRecipient);

        if (!url) {
            return;
        }

        if(this.customContent) {
            url += `?custom_content=${encodeURIComponent(this.customContent)}`
        }

        this.previewLoading = true;

        this.api.get(url)
            .then(({data}) => {
                if(nowPreviewCounter === this.currentPreviewCounter) {
                    this.previewData = data.data;
                    this._updatePreviewAttachments();
                    this.previewLoading = false;
                }
            })
            .catch((e) => {
                this.previewLoadError = true;
                this.previewLoading = false;
            })
    }

    _getPreviewUrl(recipient) {
        return '';
    }

    _getPreviewRecipient() {
        return null;
    }

    _showMessageQueuedAlert() {
        this.alert.animatedAlert(
            this.translate.instant('core.helpers.routing.success_message_queued.title'),
            this.translate.instant('core.helpers.routing.success_message_queued.content'),
            'send',
            {
                confirmButtonText: this.translate.instant('core.helpers.routing.success_message_queued.confirm'),
                showCancelButton: false,
                confirmButtonColor: '#9C63FF'
            }
        );
    }

    _handleEmptyTemplates() {
        this.modalInstance.dismiss();

        this.alert
            .animatedAlert(
                this.translate.instant('messages.controllers.abstract-message-modal.no-templates-available.title'),
                this.translate.instant('messages.controllers.abstract-message-modal.no-templates-available.content'),
                'attention',
                {
                    confirmButtonText: this.translate.instant('messages.controllers.abstract-message-modal.no-templates-available.add_template'),
                    cancelButtonText: this.translate.instant('general.buttons.cancel'),
                    confirmButtonColor: '#e01249'
                }
            )
            .then(() => {
                this.state.go('app.templates.index', {
                    startTab: 'shared'
                });
            })
    }
}

AbstractMessageModalController.$inject = [
    '$uibModalInstance',
    '$injector',
    '$scope',
    'extendedInformation'
];



// WEBPACK FOOTER //
// ./src/app/modules/messages/controllers/modals/abstract-message-modal.js